import React, { useState, useEffect, useCallback } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import "./css/LandingPage-Animation.css";
import VideoPlayer from "./VideoPlayer";
import ParticleBackground from "./ParticleBackground";

const NUDGE_MESSAGES = [
  {
    id: 1,
    headline: "Your Instincts Are Good — But Scammers Are Better",
    body: `Every day, you see another headline — a neighbor lost their savings to a phone scam, a friend's parent wired money to a fake "grandchild." You've probably thought, "That wouldn't happen to me," but the truth is, today's scams are engineered by professionals who study exactly how to bypass smart people's defenses. The feeling that something "seems off" about a message or call? That instinct is real — but it fires too late about half the time.\n\nThat's exactly why KinShield exists. It's not about assuming you can't spot a scam — it's about giving your good judgment a second set of eyes. The same way you'd lock your front door even in a safe neighborhood, KinShield quietly analyzes the emails, texts, and calls that reach you and flags the ones designed to manipulate. Thousands of fraud attempts target adults over 50 every single day, and the people who get caught aren't careless — they're just human. Signing up for KinShield isn't admitting vulnerability; it's the same kind of move a sharp, prepared person makes. Try it free today and let your instincts work with real-time intelligence behind them.`,
  },
  {
    id: 2,
    headline: "The Protection Your Family Deserves",
    body: `You already do so much to look out for the people you love — checking in, helping with appointments, staying connected. But there's one threat most families don't talk about until it's too late: the scam call that sounds perfectly real, the email that looks exactly like it came from Medicare or the bank. These aren't the clumsy scams of ten years ago. They're polished, personal, and designed to create just enough urgency that even cautious people act before they think. The hardest part? Your loved ones may never mention the close calls — because admitting "I almost fell for it" feels embarrassing, even when it shouldn't.\n\nKinShield takes that pressure off the table entirely. It works quietly in the background, checking suspicious messages and calls before they become costly mistakes. You're not hovering, and they're not asking for help — it's just a layer of protection that's already there. Families who use KinShield aren't more fearful; they just sleep a little easier. Give it a try — it's the kind of thing you'll wish you had set up sooner.`,
  },
  {
    id: 3,
    headline: "The Gap Between Confidence and Protection Is One Phone Call",
    body: `Right now, someone is reading a script with your area code, your bank's name, and a story convincing enough to make your heart race. They've done this four hundred times this week. They know exactly when to pause, when to sound concerned, and when to say "we just need to verify your account before the funds are gone." They don't target people who are careless — they target people who are responsible, because responsible people act fast when they think something is wrong. That's not a guess. That's FBI data.\n\nHere's what nobody tells you: the moment you realize it's a scam is almost never during the call. It's ten minutes later. It's lying in bed that night. It's the sick feeling when the bank says the wire can't be reversed. The average loss for adults over 50 is now over $34,000 per incident — not because victims are naive, but because these operations are run like businesses, with call centers, training manuals, and AI tools that clone voices and forge documents in seconds. The gap between "that could never happen to me" and "I can't believe that just happened to me" is exactly one convincing phone call.\n\nKinShield closes that gap. You forward a suspicious text, paste a sketchy email, or upload a document that doesn't feel right — and within seconds, KinShield's AI breaks down every manipulation tactic, every red flag, every detail your instincts noticed but couldn't name. No jargon. No lectures. Just a clear answer: this is real, or this is a scam, and here's exactly why. You stay in control. You make the call. But you make it with the same intelligence the fraudsters are using — turned around and pointed right back at them.\n\nThe people who sign up for KinShield aren't scared. They're the same people who lock the car in their own driveway, who check the expiration date even at a nice restaurant, who read the fine print because they know that's where the truth hides. It costs less per month than the cup of coffee you'll drink while reading this. And unlike that coffee, it might save you everything in your checking account. Start your free trial today — because the only scam worse than the one in your inbox is convincing yourself you don't need protection from it.`,
  },
];

function LandingPageAnimation({ onManageAccountClick }) {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [showPhone, setShowPhone] = useState(true);
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [showNudge, setShowNudge] = useState(false);
  const [activeNudge, setActiveNudge] = useState(null);
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  // Lock body scroll when nudge panel is open (prevents mobile layout shift)
  useEffect(() => {
    if (showNudge) {
      const scrollY = window.scrollY;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      return () => {
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [showNudge]);

  const handleCtaHookClick = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * NUDGE_MESSAGES.length);
    setActiveNudge(NUDGE_MESSAGES[randomIndex]);
    setShowNudge(true);
  }, []);

  const closeNudge = useCallback(() => {
    setShowNudge(false);
  }, []);

  const scamMessages = [
    {
      id: 1,
      text: "URGENT: Your bank account has been compromised. Click here to verify: bit.ly/secure123",
      time: "2:34 PM",
    },
    {
      id: 2,
      text: "Congratulations! You've won $5,000. Reply with your SSN to claim your prize!",
      time: "2:35 PM",
    },
    {
      id: 3,
      text: "IRS Notice: You owe back taxes. Pay immediately to avoid arrest. Call: 555-0123",
      time: "2:36 PM",
    },
    {
      id: 4,
      text: "Your package is held at customs. Pay $50 fee now: suspicious-link.com",
      time: "2:37 PM",
    },
  ];

  useEffect(() => {
    if (isInView) {
      // Show messages one by one
      scamMessages.forEach((msg, index) => {
        setTimeout(() => {
          setVisibleMessages((prev) => [...prev, msg]);
        }, index * 600);
      });

      // Hide phone and show main image after subtitle appears
      setTimeout(() => {
        setShowPhone(false);
      }, 3200);
    }
  }, [isInView]);

  const openVideoModal = () => setIsVideoModalOpen(true);
  const closeVideoModal = () => setIsVideoModalOpen(false);

  const handlePodcastClick = () => {
    const audioPath =
      "/Beyond_Spam__The_4-Layer_AI_Shield_Protecting_You_from_Next-Gen.m4a";
    console.log("Attempting to open audio file at:", audioPath);

    const audioWindow = window.open(audioPath, "_blank");

    if (!audioWindow) {
      console.log("Window.open failed, trying audio element approach");
      const audio = new Audio(audioPath);
      audio.onerror = () => {
        toast.error(
          "Audio file could not be loaded. Please check your internet connection or contact support.",
        );
      };
      audio.onloadstart = () => {
        console.log("Audio loading started");
      };
      audio.play().catch((error) => {
        console.error("Audio playback failed:", error);
        toast.error(
          "Audio playback failed. The file might not be available or your browser blocked autoplay.",
        );
      });
    }
  };

  // Phone screen variants - full size from start
  const phoneVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      rotateY: -90,
      transition: {
        duration: 0.4,
        ease: "easeInOut",
      },
    },
  };

  const messageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
  };

  // Animation variants - Title comes from right AFTER image appears
  const titleVariants = {
    hidden: {
      opacity: 0,
      x: window.innerWidth,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        delay: 4.7, // Phone hides at 3.2s, image appears at 3.5s, title at 4.7s
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const subtextVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 5.5, // Title at 4.7s + 0.8s duration = 5.5s for subtitle
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.9, rotateY: 90 },
    visible: {
      opacity: 1,
      scale: 1,
      rotateY: 0,
      transition: {
        delay: 0.1,
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 100 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 6.5, // Subtitle finishes at ~6.1s, buttons at 6.5s
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="landing-page-animated" ref={ref}>
      <main className="landing-main particle-container">
        <ParticleBackground
          particleCount={40}
          particleColor="rgba(2, 219, 110, 0.25)"
          particleSize={2.5}
          speed={0.3}
        />

        <div className="landing-content-wrapper">
          <div className="landing-image-container">
            <AnimatePresence mode="wait">
              {showPhone && isInView ? (
                <motion.div
                  key="phone"
                  className="phone-screen-container"
                  variants={phoneVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <div className="phone-frame">
                    <div className="phone-notch"></div>
                    <div className="phone-screen">
                      <div className="phone-header">
                        <div className="contact-info">
                          <div className="contact-avatar">👤</div>
                          <div className="contact-details">
                            <div className="contact-name">Unknown Sender</div>
                            <div className="contact-number">
                              +1 (555) 123-4567
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="messages-container">
                        {visibleMessages.map((msg) => (
                          <motion.div
                            key={msg.id}
                            className="message-bubble"
                            variants={messageVariants}
                            initial="hidden"
                            animate="visible"
                          >
                            <div className="message-text">{msg.text}</div>
                          </motion.div>
                        ))}
                      </div>
                      <div className="phone-nav-bar">
                        <div className="nav-indicator"></div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="main-image"
                  className="landing-image-wrapper"
                  variants={imageVariants}
                  initial="hidden"
                  animate={isInView && !showPhone ? "visible" : "hidden"}
                >
                  <img
                    src="/ScamAlertMomAndDaughter.png"
                    alt="Man Woman Shield"
                    className="landing-image half-size left-aligned"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="landing-content">
            <motion.h1
              variants={titleVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              Empower Your Family to Outsmart Scammers
            </motion.h1>
            <motion.h2
              variants={subtextVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              Imagine if you never had to wonder "Was that email real?" or worry
              about your loved ones clicking a malicious link. KinShield™ trains
              everyone in your family to spot scams instantly and provides AI
              backup when doubt creeps in.
            </motion.h2>
            <motion.p
              className="landing-scope-description"
              variants={subtextVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              Forward a suspicious email, text, or social media post —
              KinShield™ analyzes it in seconds. Look up shady phone numbers,
              scan images for scams, and sharpen your instincts with realistic
              scam training and real-time alerts.
            </motion.p>
            {/* Original: KinShield™ empowers your loved ones with the ability to recognize
                and avoid scams with real-world simulations, practical training,
                and real-time scam detection in emails, texts, and social media,
                ensuring they stay safe online. */}
            {/* Alternative 1: Imagine knowing your family can spot a fake "bank alert" from a mile away—even 
                when you're not there to protect them. KinShield™ builds their confidence 
                through practice and provides AI backup when it matters most. */}
            {/* Alternative 2: Imagine your parents confidently spotting a scam text message that would have 
                drained their retirement account—because they've practiced detecting it dozens 
                of times. That's the power of KinShield™. */}
            {/* Alternative 3: Imagine your aging mother laughing as she deletes a phishing email—the same one 
                that fooled her neighbor out of $50,000. KinShield™ turns your family into scam 
                experts through realistic practice and AI-powered protection.*/}
            {/* Imagine never again worrying about that late-night call saying "Mom fell for 
                another scam." KinShield™ gives your family the street smarts to recognize and 
                reject even the most sophisticated attacks.
             */}
            {/* Imagine knowing your family can spot a fake "bank alert" from a mile away—even 
                when you're not there to protect them. KinShield™ builds their confidence 
                through practice and provides AI backup when it matters most.
             */}
            {/* Imagine if your parents were as confident spotting online scams as you are—and you 
                were even better than you thought. KinShield™ sharpens everyone's scam radar with 
                realistic simulations and real-time AI protection.
             */}
            {/* Imagine if neither you nor your parents ever had to experience that sinking feeling 
                of "Did I just get scammed?" KinShield™ eliminates that fear by training your whole 
                family and watching their backs with AI-powered protection.
             */}

            <motion.div
              className="action-buttons"
              variants={buttonVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              <button className="start-trial" onClick={onManageAccountClick}>
                Start Free Trial
              </button>
              <button className="watch-demo" onClick={openVideoModal}>
                <span className="play-icon">▶</span> Watch Intro
              </button>
              {/* 
              <button className="overview-podcast" onClick={handlePodcastClick}>
                <span className="audio-icon">🎧</span> Overview Podcast
              </button> 
              */}
            </motion.div>
            <motion.p
              className="hero-micro-copy"
              style={{ marginTop: "16px" }}
              variants={buttonVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              Free 30-day Trial - Cancel anytime
            </motion.p>
          </div>
        </div>
      </main>
      {isVideoModalOpen && <VideoPlayer onCancel={closeVideoModal} />}

      {/* CTA Hook — fixed right edge */}
      <div
        className="cta-hook-tab"
        onClick={handleCtaHookClick}
        title="Why KinShield?"
      >
        <span className="cta-hook-label">Why KinShield?</span>
      </div>

      {/* Nudge Popup — slides in from the right */}
      <AnimatePresence>
        {showNudge && activeNudge && (
          <motion.div
            className="nudge-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeNudge}
          >
            <motion.div
              className="nudge-panel"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 280 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="nudge-header">
                <div className="nudge-header-left">
                  <img
                    className="nudge-shield-icon"
                    src="/KinShield.png"
                    alt="KinShield"
                  />
                  <h2>KinShield™</h2>
                </div>
                <button className="nudge-close" onClick={closeNudge}>
                  ✕
                </button>
              </div>
              <div className="nudge-body">
                <h3 className="nudge-headline">{activeNudge.headline}</h3>
                {activeNudge.body.split("\n\n").map((paragraph, i) => (
                  <p key={i} className="nudge-paragraph">
                    {paragraph}
                  </p>
                ))}
              </div>
              <div className="nudge-footer">
                <button
                  className="nudge-cta-primary"
                  onClick={onManageAccountClick}
                >
                  Start Free Trial
                </button>
                <button className="nudge-cta-secondary" onClick={closeNudge}>
                  Maybe Later
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default LandingPageAnimation;
